import { Injectable } from '@angular/core';
import { Observable, Subject, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { WebSocketService, Message } from '../auth/web-socket.service';

export interface MCDataSyncEvent {
  type: 'contact' | 'template' | 'stage' | 'channel' | 'interview' | 'meeting';
  event: 'created' | 'updated' | 'deleted';
  id: string;
  company_id?: string;
  data?: any;
}

/**
 * MC Admin Data Sync Service
 * Provides real-time data synchronization for MC Admin users within the same MC company
 * Complete isolation from RC Super Admin functionality
 */
@Injectable({
  providedIn: 'root'
})
export class MCDataSyncService {
  private dataSyncSubject = new Subject<MCDataSyncEvent>();
  private isMCAdmin = new BehaviorSubject<boolean>(false);
  private userCompanyId = new BehaviorSubject<string | null>(null);
  private initialized = false;
  
  // General data sync stream for MC Admins
  public dataSyncUpdates$ = this.dataSyncSubject.asObservable();
  
  // Observable streams for different data types - lazy initialized
  private _contactUpdates$?: Observable<MCDataSyncEvent>;
  private _templateUpdates$?: Observable<MCDataSyncEvent>;
  private _stageUpdates$?: Observable<MCDataSyncEvent>;
  private _channelUpdates$?: Observable<MCDataSyncEvent>;
  private _interviewUpdates$?: Observable<MCDataSyncEvent>;
  private _meetingUpdates$?: Observable<MCDataSyncEvent>;

  constructor(private webSocketService: WebSocketService) {
    // Delay initialization to avoid circular dependency
    setTimeout(() => this.initialize(), 0);
  }

  /**
   * Initialize the service and WebSocket listener
   */
  private initialize(): void {
    if (this.initialized) return;
    this.initialized = true;
    this.initializeWebSocketListener();
  }

  // Lazy getters for observables
  get contactUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._contactUpdates$) {
      this._contactUpdates$ = this.getUpdatesForType('contact');
    }
    return this._contactUpdates$;
  }

  get templateUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._templateUpdates$) {
      this._templateUpdates$ = this.getUpdatesForType('template');
    }
    return this._templateUpdates$;
  }

  get stageUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._stageUpdates$) {
      this._stageUpdates$ = this.getUpdatesForType('stage');
    }
    return this._stageUpdates$;
  }

  get channelUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._channelUpdates$) {
      this._channelUpdates$ = this.getUpdatesForType('channel');
    }
    return this._channelUpdates$;
  }

  get interviewUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._interviewUpdates$) {
      this._interviewUpdates$ = this.getUpdatesForType('interview');
    }
    return this._interviewUpdates$;
  }

  get meetingUpdates$(): Observable<MCDataSyncEvent> {
    if (!this._meetingUpdates$) {
      this._meetingUpdates$ = this.getUpdatesForType('meeting');
    }
    return this._meetingUpdates$;
  }

  /**
   * Initialize WebSocket listener for MC Admin data sync events
   */
  private initializeWebSocketListener(): void {
    this.webSocketService.messages$.subscribe((message: Message) => {
      // Only process messages if user is MC Admin
      if (this.isMCAdmin.value && this.isMCDataSyncMessage(message)) {
        const syncEvent = this.parseMCDataSyncEvent(message);
        if (syncEvent && this.shouldProcessEvent(syncEvent)) {
          console.log('[MCDataSync] Received MC Admin sync event:', syncEvent);
          this.dataSyncSubject.next(syncEvent);
        }
      }
    });
  }

  /**
   * Check if message is an MC data sync event
   */
  private isMCDataSyncMessage(message: Message): boolean {
    return ['contact', 'template', 'stage', 'channel', 'interview', 'meeting'].includes(message.type) &&
           ['created', 'updated', 'deleted'].includes(message['event']);
  }

  /**
   * Parse WebSocket message into MC data sync event
   */
  private parseMCDataSyncEvent(message: Message): MCDataSyncEvent | null {
    try {
      const event: MCDataSyncEvent = {
        type: message.type as any,
        event: message['event'] as any,
        id: message['contact_id'] || message['template_id'] || message['stage_id'] || 
            message['channel_id'] || message['interview_id'] || message['meeting_id'] || '',
        company_id: message['company_id'],
        data: message['data']
      };
      
      return event.id ? event : null;
    } catch (error) {
      console.error('[MCDataSync] Error parsing sync event:', error);
      return null;
    }
  }

  /**
   * Check if the sync event should be processed by this MC Admin
   * Only process events from the same company
   */
  private shouldProcessEvent(event: MCDataSyncEvent): boolean {
    const currentCompanyId = this.userCompanyId.value;
    
    // Only process events from same company
    if (!currentCompanyId || !event.company_id) {
      return false;
    }
    
    return event.company_id === currentCompanyId;
  }

  /**
   * Get updates stream for specific data type
   */
  private getUpdatesForType(type: MCDataSyncEvent['type']): Observable<MCDataSyncEvent> {
    return this.dataSyncUpdates$.pipe(
      filter(event => event.type === type)
    );
  }

  /**
   * Set whether current user is MC Admin and their company ID
   */
  public setMCAdminStatus(isMCAdmin: boolean, companyId?: string): void {
    this.isMCAdmin.next(isMCAdmin);
    this.userCompanyId.next(companyId || null);
    
    console.log('[MCDataSync] MC Admin status updated:', { isMCAdmin, companyId });
  }

  /**
   * Get MC Admin status
   */
  public getMCAdminStatus(): Observable<boolean> {
    return this.isMCAdmin.asObservable();
  }

  /**
   * Get current company ID
   */
  public getCompanyId(): Observable<string | null> {
    return this.userCompanyId.asObservable();
  }

  /**
   * Manually trigger data refresh (useful for non-real-time updates)
   */
  public triggerDataRefresh(type: MCDataSyncEvent['type']): void {
    if (!this.isMCAdmin.value) {
      console.warn('[MCDataSync] Cannot trigger refresh - user is not MC Admin');
      return;
    }
    
    const refreshEvent: MCDataSyncEvent = {
      type,
      event: 'updated',
      id: 'manual-refresh',
      company_id: this.userCompanyId.value || undefined
    };
    
    this.dataSyncSubject.next(refreshEvent);
  }

  /**
   * Check if current user should receive MC data sync events
   */
  public shouldReceiveMCSync(): boolean {
    return this.isMCAdmin.value && this.userCompanyId.value !== null;
  }

  /**
   * Reset the service state (useful for logout)
   */
  public resetState(): void {
    this.isMCAdmin.next(false);
    this.userCompanyId.next(null);
    console.log('[MCDataSync] Service state reset');
  }
}